import React, { useEffect, useState, useContext } from "react";
import { postData } from "../../../../services/Fetchnodeservices";
import { ThemeContext } from "../../../../context/ThemeContext";
import SummaryCard from "./components/SummaryCard";
import PendingOrdersTable from "./components/PendingOrdersTable";
import DepositHistoryTable from "./components/DepositHistoryTable";
import DepositModal from "./components/DepositModal";
import { getStoredAdminId } from "../../../../utils/adminSession";

const CashDashboard = ({ driverId }) => {
  const { theme } = useContext(ThemeContext); // ← Theme Context

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    cash_in_hand: 0,
    pending_orders: 0,
    total_deposited: 0,
    pending_verification: 0,
  });

  const [pendingOrders, setPendingOrders] = useState([]);
  const [depositHistory, setDepositHistory] = useState([]);

  const totalSelectedAmount = selectedOrders.reduce(
    (sum, item) => sum + Number(item.final_amount || 0),
    0
  );

  useEffect(() => {
    if (driverId) {
      fetchCashDashboard();
    }
  }, [driverId]);

  const fetchCashDashboard = async () => {
    try {
      setLoading(true);
      const result = await postData("driver/driver/cash-dashboard", {
        driverId,
      });
      console.log("Cash Dashboard :", result);
      if (result.success) {
        setSummary(result.summary || {});
        setPendingOrders(result.pendingOrders || []);
        setDepositHistory(result.depositHistory || []);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const openDepositModal = (orders) => {
    setSelectedOrder(orders);
    setShowDepositModal(true);
  };

  const closeDepositModal = () => {
    setSelectedOrder(null);
    setShowDepositModal(false);
  };

  const toggleOrder = (order) => {
    setSelectedOrders((prev) => {
      const exists = prev.find((o) => o.id === order.id);
      if (exists) {
        return prev.filter((o) => o.id !== order.id);
      }
      return [...prev, order];
    });
  };

  const formatMoney = (amount) => {
    return `₹${Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const submitDeposit = async ({ orders, notes }) => {
    try {
      setLoading(true);
      const collectedByAdminId = getStoredAdminId();

      if (!collectedByAdminId) {
        alert("Admin session is invalid. Please logout and login again.");
        return;
      }

      const result = await postData("driver/driver/deposit-cash", {
        driverId,
        orders,
        notes,
        collectedByAdminId,
      });

      if (result.success) {
        closeDepositModal();
        setSelectedOrders([]);
        await fetchCashDashboard();
      } else {
        alert(result.message || result.error || "Deposit Failed");
      }
    } catch (err) {
      console.log(err);
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // THEME-AWARE STYLES
  // ─────────────────────────────────────────────────────────────────────────────
  const colors = {
    bg: theme.background,
    border: theme.border,
    text: theme.textPrimary,
    light: theme.textMuted,
  };

  const thStyle = {
    textAlign: "left",
    padding: 14,
    fontSize: 13,
    borderBottom: `1px solid ${theme.border}`,
    color: theme.primary,
    background: theme.surfaceLighter,
  };

  const tdStyle = {
    padding: 14,
    fontSize: 13,
    borderBottom: `1px solid ${theme.border}`,
    color: theme.textPrimary,
  };

  if (loading) {
    return (
      <div
        style={{
          padding: 40,
          textAlign: "center",
          fontSize: 16,
          color: theme.textMuted,
          background: theme.surface,
          borderRadius: "16px",
          border: `1px solid ${theme.border}`,
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${theme.border}`,
            borderTopColor: theme.primary,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            margin: "0 auto 16px",
          }}
        />
        Loading Cash Dashboard...
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Summary Cards Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 20,
          marginBottom: 25,
        }}
      >
        <SummaryCard
          title="Cash In Hand"
          value={formatMoney(summary.cash_in_hand)}
          icon="💰"
          color={theme.success}
          theme={theme}
        />
        <SummaryCard
          title="Pending Orders"
          value={summary.pending_orders}
          icon="📦"
          color={theme.primary}
          theme={theme}
        />
        <SummaryCard
          title="Total Deposited"
          value={formatMoney(summary.total_deposited)}
          icon="🏦"
          color="#9333ea"
          theme={theme}
        />
        <SummaryCard
          title="Pending Verification"
          value={summary.pending_verification}
          icon="⏳"
          color={theme.warning}
          theme={theme}
        />
      </div>

      {/* Pending Orders Table */}
      <PendingOrdersTable
        pendingOrders={pendingOrders}
        theme={theme}
        formatMoney={formatMoney}
        formatDate={formatDate}
        openDepositModal={openDepositModal}
        selectedOrders={selectedOrders}
        toggleOrder={toggleOrder}
        totalSelectedAmount={totalSelectedAmount}
      />

      {/* Deposit History Table */}
      <DepositHistoryTable
        depositHistory={depositHistory}
        theme={theme}
        formatMoney={formatMoney}
        formatDate={formatDate}
      />

      {/* Deposit Modal */}
      <DepositModal
        open={showDepositModal}
        onClose={closeDepositModal}
        selectedOrder={selectedOrder}
        theme={theme}
        onSubmit={submitDeposit}
      />
    </div>
  );
};

export default CashDashboard;
